import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { FormularioInforme } from '../components/ui';
import { useCrearInforme } from '../hooks/useCrearInforme';
import { FormularioInforme as FormularioInformeType } from '../types/informe';

/**
 * Página para crear un nuevo informe de jugador
 */
const CrearInforme: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { crearInforme, isLoading } = useCrearInforme();
  const [mostrarInfoPartido, setMostrarInfoPartido] = useState(false);
  
  // Obtener datos del partido desde la navegación
  const partidoData = location.state?.partidoData;

  // Validar que existe el partido
  React.useEffect(() => {
    if (!partidoData?.id) {
      navigate('/', { 
        state: { 
          message: 'Error: No se encontró información del partido',
          type: 'error'
        }
      });
    }
  }, [partidoData, navigate]);

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (formData: FormularioInformeType) => {
    if (!partidoData?.id) {
      console.error('Error: partidoId no disponible');
      return;
    }

    try {
      await crearInforme({
        ...formData,
        partidoId: partidoData.id
      });
      
      // Navegar a captación con mensaje de éxito
      navigate('/partidos', { 
        state: { 
          message: 'Informe creado exitosamente',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error al crear informe:', error);
    }
  };

  /**
   * Alterna la visibilidad de la información del partido
   */
  const toggleInfoPartido = () => {
    setMostrarInfoPartido(!mostrarInfoPartido);
  };

  if (!partidoData?.id) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-400">Error: No se encontró información del partido</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Botón para mostrar/ocultar información del partido */}
      {partidoData && (
        <div className="mb-6">
          <button
            onClick={toggleInfoPartido}
            className="w-full bg-secondary-800 hover:bg-secondary-700 rounded-lg p-4 border border-secondary-700 transition-colors flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Info size={20} className="text-primary-400" />
              <span className="text-white font-semibold">
                {mostrarInfoPartido ? 'Ocultar' : 'Mostrar'} Información del Partido
              </span>
            </div>
            {mostrarInfoPartido ? (
              <ChevronUp size={20} className="text-gray-400" />
            ) : (
              <ChevronDown size={20} className="text-gray-400" />
            )}
          </button>
          
          {/* Información del Partido (colapsible) */}
          {mostrarInfoPartido && (
            <div className="bg-secondary-800 rounded-lg p-4 mt-2 border border-secondary-700 animate-in slide-in-from-top-2 duration-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Categoría:</span>
                  <span className="text-white ml-2">{partidoData.categoria}</span>
                </div>
                <div>
                  <span className="text-gray-400">Grupo:</span>
                  <span className="text-white ml-2">{partidoData.grupo}</span>
                </div>
                <div>
                  <span className="text-gray-400">Fecha:</span>
                  <span className="text-white ml-2">
                    {new Date(partidoData.fechaHora).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Hora:</span>
                  <span className="text-white ml-2">
                    {new Date(partidoData.fechaHora).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-secondary-700">
                <span className="text-gray-400">Competición:</span>
                <span className="text-white ml-2">{partidoData.competicion}</span>
              </div>
              <div className="mt-2">
                <span className="text-gray-400">Partido:</span>
                <span className="text-white ml-2">{partidoData.nombre}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Formulario */}
      <div className="bg-secondary-800 rounded-lg p-6 border border-secondary-700">
        <FormularioInforme
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default CrearInforme;